/**
 * calendarTime — reine, testbare Zeit-/Kalender-Helfer für KalenderPage.
 * Bewusst ohne React/DOM, damit per Vitest abgesichert (verhindert u.a. den
 * Zeitzonen-Bug beim Speichern und Fehler beim 30-Min-Snap / KW-Berechnung).
 */

export type CalendarView = 'day' | 'week' | 'month' | 'agenda';

const DAY_MS = 86400000;

/** Lokales Datum auf Mitternacht (00:00) gesetzt. */
export function startOfDayLocal(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Montag (ISO-Wochenstart) der Woche, in der d liegt. */
export function mondayOf(d: Date): Date {
  const m = startOfDayLocal(d);
  const off = (m.getDay() + 6) % 7;
  m.setDate(m.getDate() - off);
  return m;
}

/** ISO-8601 Kalenderwoche (1–53). */
export function getISOWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const ftDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ftDayNum + 3);
  return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * DAY_MS));
}

/** Micro-Relativ-Hinweis ausgehend von heute, je Ansicht. */
export function relativeLabel(view: CalendarView, current: Date, today: Date = new Date()): string {
  const t = startOfDayLocal(today);
  const cur = startOfDayLocal(current);
  if (view === 'day') {
    const diff = Math.round((cur.getTime() - t.getTime()) / DAY_MS);
    return diff === 0 ? 'heute' : diff === 1 ? 'morgen' : diff === -1 ? 'gestern'
      : diff > 0 ? `in ${diff} Tagen` : `vor ${-diff} Tagen`;
  }
  if (view === 'week') {
    const diffW = Math.round((mondayOf(cur).getTime() - mondayOf(t).getTime()) / (7 * DAY_MS));
    return diffW === 0 ? 'diese Woche' : diffW === 1 ? 'nächste Woche' : diffW === -1 ? 'letzte Woche'
      : diffW > 0 ? `in ${diffW} Wochen` : `vor ${-diffW} Wochen`;
  }
  if (view === 'month') {
    const diffM = (cur.getFullYear() - t.getFullYear()) * 12 + (cur.getMonth() - t.getMonth());
    return diffM === 0 ? 'dieser Monat' : diffM === 1 ? 'nächster Monat' : diffM === -1 ? 'letzter Monat'
      : diffM > 0 ? `in ${diffM} Monaten` : `vor ${-diffM} Monaten`;
  }
  return 'ab heute';
}

/** Snappt eine (Dezimal-)Stunde auf 30 Minuten und klemmt in [minHour, maxHour]. */
export function snapHalfHour(rawHour: number, minHour: number, maxHour: number): number {
  return Math.max(minHour, Math.min(maxHour, Math.round(rawHour * 2) / 2));
}

/** Zerlegt eine Dezimal-Stunde in volle Stunde + Minute. */
export function splitHour(h: number): { hour: number; minute: number } {
  const hour = Math.floor(h);
  return { hour, minute: Math.round((h - hour) * 60) };
}

/** Dauer eines Termins in Stunden (Default 1h ohne Ende, Minimum 0.5h). */
export function eventDurationHours(startISO: string, endISO: string | null | undefined): number {
  const s = new Date(startISO);
  const e = endISO ? new Date(endISO) : new Date(s.getTime() + 3600000);
  return Math.max(0.5, (e.getTime() - s.getTime()) / 3600000);
}
